import type { ConversationMessage } from "@/types/ai-conversation";

/**
 * Normalize an email thread into ConversationMessage[].
 * Passes ALL emails in the thread (no limit).
 * Role derived by comparing from_attendee.identifier against user emails.
 */
export function normalizeEmailThread(
  threadEmails: any[],
  userEmails: string[]
): ConversationMessage[] {
  if (!threadEmails?.length) return [];

  const lowerUserEmails = userEmails.filter(Boolean).map((e) => e.toLowerCase().trim());

  return threadEmails.map((email) => {
    const fromEmail = (email.from_attendee?.identifier || email.from_attendee?.email || "")
      .toLowerCase()
      .trim();

    const isUser = lowerUserEmails.some((ue) => fromEmail.includes(ue) || ue.includes(fromEmail));

    return {
      role: isUser ? "user" : "contact",
      text: email.body_plain || email.body || email.snippet || "",
      timestamp: email.date || email.created_at || new Date().toISOString(),
      subject: email.subject || undefined,
    } as ConversationMessage;
  });
}

/**
 * Normalize WhatsApp messages into ConversationMessage[].
 * Takes last 20 messages. Role derived from is_sender / sender_id / isOwn.
 */
export function normalizeWhatsAppMessages(messages: any[]): ConversationMessage[] {
  if (!messages?.length) return [];

  return messages.slice(-20).map((msg) => ({
    role: msg.is_sender || msg.sender_id === "me" || msg.isOwn ? "user" : "contact",
    text: msg.text || msg.body || msg.content || "",
    timestamp: msg.timestamp || msg.date || msg.created_at || new Date().toISOString(),
  })) as ConversationMessage[];
}

/**
 * Normalize LinkedIn messages into ConversationMessage[].
 * Takes last 20 messages. Role derived from is_sender / sender_id / isOwn.
 */
export function normalizeLinkedInMessages(messages: any[]): ConversationMessage[] {
  if (!messages?.length) return [];

  return messages.slice(-20).map((msg) => ({
    role: msg.is_sender || msg.sender_id === "me" || msg.isOwn ? "user" : "contact",
    text: msg.text || msg.body || msg.content || "",
    timestamp: msg.timestamp || msg.date || msg.created_at || new Date().toISOString(),
  })) as ConversationMessage[];
}

/**
 * Normalize SMS messages into ConversationMessage[].
 * Takes last 20 messages. Role derived from direction (outbound = user).
 */
export function normalizeSMSMessages(messages: any[]): ConversationMessage[] {
  if (!messages?.length) return [];

  return messages.slice(-20).map((msg) => ({
    role: msg.direction === "outbound" ? "user" : "contact",
    text: msg.body || msg.text || msg.content || "",
    timestamp: msg.sentAt || msg.timestamp || msg.created_at || new Date().toISOString(),
  })) as ConversationMessage[];
}
